import React, { Component, useRef } from "react";
import styled from "styled-components"
import PropTypes from "prop-types"
import { inject, observer } from "mobx-react"

import CheckoutButton from "./CheckoutButton"
import TinySliderIconAndText from "~/components/sliders/TinySliderIconAndText"
import TermsOfServiceAgreement from "~/components/payment/TermsOfServiceAgreement"
import Stationary from "~/components/svg/Stationary"
import Presenter from "~/components/svg/Presenter"
import Clock from "~/components/svg/Clock"
import Lock from '~/components/svg/Lock'
import { PAYMENT_PROVIDERS } from "~/utilities/constants"
import { showCkoFlowDropin } from "~/stores/paymentFlowActions"
import ThreeDSLockIcon from "~/components/svg/ThreeDSLockIcon"

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${props =>
    props.isMobile
      ? `
    background: none;
    box-shadow: 0 9px 24px 0 rgba(35,41,48,0);
    padding: 0rem;
  `
      : `
    background: ${props.theme.colors.background.page};
    box-shadow: ${props.theme.boxShadow.basic};
    border-radius: 4px;
  `}

  ${props => props.theme.media.smallDesktop`
    justify-content: center;
  `}
`

const Container = styled.div`
  display: flex;
  transition: all 0.5s ease;
  flex-direction: row;
  margin-bottom: 5px;
  ${props => props.theme.media.smallDesktop`
    flex-direction: column;
  `}
`

const Left = styled.div`
  display: flex;
  flex-grow: 1;
  flex-shrink: 1;
`

const Right = styled.div`
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  width: 250px;
  ${props => props.theme.media.smallDesktop`
    ${
      props.hide
        ? `
      display: none;
      `
        : null
    }
      width: 100%;
  `}
`

const LeftContainer = styled.div`
  background: none;
  display: flex;
  padding: ${props => (props.isMobile || props.reducePadding ? "0" : "2rem")};
  flex-direction: column;
  width: 100%;
`

const RightContainer = styled.div`
  display: flex;
  position: relative;
  padding: 1rem 0;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background-color: ${props => props.theme.colors.background.main};
  border-radius: 0 4px 4px 0;
  width: 100%;
  ${props => props.theme.media.smallDesktop`
    background: none;
  `}
`

const SliderContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

// Dev-only: relative wrapper + transparent overlay so a click on the disabled
// pay button can be captured (disabled buttons swallow click events). The
// overlay only renders when the button is not submittable and logs why.
const PayButtonWrap = styled.div`
  position: relative;
  display: inline-flex;
`

const DisabledClickCatcher = styled.div`
  position: absolute;
  inset: 0;
  cursor: not-allowed;
  background: transparent;
`

const BTCheckoutButton = styled.button`
  width: 90%;
  height: 60px;
  font-size: 1.125rem;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  border: none;
  user-select: none;
  outline: none;
  transition: height 0.5s ease;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  color: #FFFFFF;
  :hover {
    box-shadow: 0 9px 24px 0 rgba(35,41,48,0.15);
    background: linear-gradient(to bottom right, #5ac866 0%, #71d77c 100%);
  }
  background: linear-gradient(135deg, #71D77C, #5AC866);
  box-shadow: 0px 14px 24px rgba(98,211,109,0.39);
  ${props => props.theme.media.mobile`
    width: 100%;
  `}
`

const IconContainer = styled.div`
  margin-right: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
`

const FIGHT_ITEMS = [
  {
    text: "No charge now and your card will be verified with 3D Secure",
    Icon: ThreeDSLockIcon
  },
  {
    text: "Dont worry, no late fees, and we will keep you posted",
    Icon: Stationary
  },
  {
    text: "Our experts will review your tickets",
    Icon: Presenter
  },
  {
    text: "Be patient, it will take some time",
    Icon: Clock
  }
]

// Remove 2nd bullet point for Evervault
const PAY_ITEMS = [
  {
    text: "Secure and easy way to pay",
    Icon: Stationary
  },
  // {
  //   text: "We pay NYC on your behalf",
  //   Icon: Presenter
  // },
  {
    text: "Payments submitted same day",
    Icon: Clock
  }
]

@inject(all => ({
  isMobile: all.store.ui.isMobile,
  isFightFlow: all.store.parkingFlow.flow.isFightFlow,
  isPayFlow: all.store.parkingFlow.flow.isPayFlow,
  isRobotPayFlow: all.store.parkingFlow.flow.isRobotPayFlow,
  ticketsFlow: all.store.parkingFlow.flow.ticketIdsForFlow,
  checkout: all.store.payment.checkout,
  checkoutWithFlow: all.store.payment.checkoutWithFlow,
  payment: all.store.payment,
  allowSubmit: all.store.payment.allowSubmit,
  showErrorToast: all.store.payment.showErrorToast,
  payment: all.store.payment,
}))
@observer
class PaymentFormContainer extends Component {

  // Log why the pay button is disabled/no-op when a user clicks it.
  logSubmitDisabledReasons = () => {
    const { payment } = this.props
    const reasons = payment?.submitDisabledReasons || []
    if (payment?.isLoading && !reasons.includes("request loading in progress")) {
      reasons.push(
        `form loading in progress (isLoadingForm=${!!payment?.braintree?.isLoadingForm})`
      )
    }
    // eslint-disable-next-line no-console
    console.error(
      "[PaymentForm] pay button click had no effect — reasons:",
      reasons.length ? reasons : ["unknown — allowSubmit and isLoading both falsy, check checkout() early returns"]
    )
  }

  render() {
    const {
      children,
      isMobile,
      isLoading,
      allowSubmit,
      ticketsFlow,
      checkout,
      checkoutWithFlow,
      payment,
      isFightFlow,
      isPayFlow,
      isRobotPayFlow,
      paymentProcessor,
      showErrorToast,
    } = this.props
    let bulletPoints
    if (isFightFlow) {
      if (paymentProcessor === PAYMENT_PROVIDERS.stripe) {
        bulletPoints = FIGHT_ITEMS
      } else {
        // If not stripe, exclude the first bullet point about 3DS
        bulletPoints = FIGHT_ITEMS.filter((item, index) => index !== 0)
      }
    }
    if (isPayFlow || isRobotPayFlow) bulletPoints = PAY_ITEMS

    let actionText
    if (isFightFlow) actionText = ticketsFlow.length === 1 ? "Fight My Ticket" : "Fight My Tickets"
    if (isPayFlow || isRobotPayFlow) actionText = ticketsFlow.length === 1 ? "Pay My Ticket" : "Pay My Tickets"

    const ckoCheckout = async () => {
      if (paymentProcessor === PAYMENT_PROVIDERS.evervault && (isPayFlow || isRobotPayFlow)) {
        return null
      }
      if (showCkoFlowDropin(paymentProcessor, {
        isFightFlow,
        isPayFlow,
        isRobotPayFlow,
      }) && payment.flowComponent) {
        try {
          await checkoutWithFlow()
        } catch (error) {
          showErrorToast(error?.message || "Payment failed. Please try again.")
        }
        return
      }
      showErrorToast("Payment form is not ready yet. Please wait a moment and try again.")
    }

    let CheckoutButtonComponent

    // BT drop-in component looks for this ID in the DOM to register its callback to tokenize the card
    if (paymentProcessor === PAYMENT_PROVIDERS.braintree) {
      CheckoutButtonComponent = (
        <BTCheckoutButton
          id="submit-button"
          disabled={isLoading || !allowSubmit}
        >
          <IconContainer>
            <Lock color="#FFFFFF" />
          </IconContainer>
          {isLoading ? "Please wait..." : actionText}
        </BTCheckoutButton>
      )
    }

    // Stripe is handled internally via payment store,
    // CKO drop-in exposes submitCard() to tokenize & initiate payment w/ the backend:
    // Todo: dedicated button for CKO?
    if (paymentProcessor === PAYMENT_PROVIDERS.stripe || paymentProcessor === PAYMENT_PROVIDERS.cko || paymentProcessor === PAYMENT_PROVIDERS.evervault) {
      CheckoutButtonComponent = (
        <CheckoutButton
          id="submit-button"
          allowSubmit={allowSubmit}
          disabled={isLoading || !allowSubmit}
          onClick={paymentProcessor === PAYMENT_PROVIDERS.stripe ? checkout : ckoCheckout}
          text={actionText}
        />
      )
    }

    return (
      <>
        <MainContainer isMobile={isMobile}>
          <Container isMobile={isMobile}>
            <Left>
              <LeftContainer isMobile={isMobile}>{children}</LeftContainer>
            </Left>
            <Right hide>
              <RightContainer isMobile={isMobile}>
                <SliderContainer>
                  <TinySliderIconAndText
                    big
                    isMobile={isMobile}
                    items={bulletPoints}
                  />
                </SliderContainer>
              </RightContainer>
            </Right>
          </Container>
          <Container>
            <Left>
              <LeftContainer reducePadding>
                <TermsOfServiceAgreement actionText={actionText} />
              </LeftContainer>
            </Left>
            <Right>
              <RightContainer>
                <PayButtonWrap>
                  {CheckoutButtonComponent}
                  {(isLoading || !allowSubmit) && (
                    <DisabledClickCatcher
                      onClick={this.logSubmitDisabledReasons}
                      aria-hidden="true"
                    />
                  )}
                </PayButtonWrap>
              </RightContainer>
            </Right>
          </Container>
        </MainContainer>
      </>
    )
  }
}

PaymentFormContainer.defaultProps = {
  isMobile: false
}
PaymentFormContainer.propTypes = {
  isMobile: PropTypes.bool
}

export default PaymentFormContainer
